import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ownerApi, formatDetail } from "../api";
import {
  ArrowLeft, Loader2, PauseCircle, Archive, RotateCcw, Trash2, Save,
  CheckCircle2, XCircle, KeyRound, Copy, Check,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const TIER_LABEL = {
  level_1: "Level I — Facilitation",
  level_2: "Level II — Curriculum",
  level_3: "Level III — Research",
};
const FEATURE_KEYS = [
  ["learner_management", "Learner Management"],
  ["events", "Events"],
  ["lesson_planner", "Lesson Planner"],
  ["resource_library", "Resource Library"],
  ["curriculum", "Curriculum Management"],
  ["question_bank", "Question Bank"],
  ["historical_assessments", "Historical Assessments"],
  ["gradebook", "Gradebook"],
  ["surveys", "Surveys"],
  ["analytics", "Analytics"],
  ["learning_logic", "Learning Logic"],
  ["epic_practice", "Sandbox"],
];

function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a), bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

export default function OrganizationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [presets, setPresets] = useState(null);          // from platform-settings
  const [pending, setPending] = useState(null);          // {name, tier, flags}
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([
        ownerApi.get(`/organizations/${id}`),
        ownerApi.get("/platform-settings"),
      ]);
      setOrg(o.data);
      setPresets(s.data.feature_presets || {});
      setPending({
        name: o.data.name,
        tier: o.data.preset_tier,
        flags: { ...(o.data.feature_flags || {}) },
      });
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Load failed");
      setOrg({ error: true });
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const dirty = useMemo(() => {
    if (!org || !pending) return false;
    return (
      pending.name !== org.name ||
      pending.tier !== org.preset_tier ||
      !shallowEqual(pending.flags, org.feature_flags || {})
    );
  }, [org, pending]);

  if (!org) return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>;
  if (org.error) return <p className="text-red-400">Not found</p>;

  const onTierChange = (newTier) => {
    const preset = presets?.[newTier];
    setPending((p) => ({
      ...p,
      tier: newTier,
      // Reset flags to that tier's preset whenever tier changes. User can
      // further tweak after this; all changes remain pending until Save.
      flags: preset ? { ...preset } : p.flags,
    }));
  };

  const toggleFlag = (key) => {
    setPending((p) => ({ ...p, flags: { ...p.flags, [key]: !p.flags[key] } }));
  };

  const resetPending = () => {
    setPending({
      name: org.name,
      tier: org.preset_tier,
      flags: { ...(org.feature_flags || {}) },
    });
  };

  const commit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (pending.name !== org.name) payload.name = pending.name;
      if (pending.tier !== org.preset_tier) payload.preset_tier = pending.tier;
      if (!shallowEqual(pending.flags, org.feature_flags || {})) payload.feature_flags = pending.flags;
      await ownerApi.put(`/organizations/${id}`, payload);
      toast.success("Organization updated");
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const transition = async (action, confirmText) => {
    if (action === "delete") {
      // Tier 3 re-auth handled by the inline DeleteConfirmDialog below; the
      // button opens the dialog instead of calling transition().
      return;
    }
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await ownerApi.post(`/organizations/${id}/${action}`);
      toast.success(`Organization ${action}d`);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const performDelete = async () => {
    try {
      await ownerApi.delete(`/organizations/${id}`);
      toast.success("Organization deleted");
      nav("/owner/organizations");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Delete failed");
      throw e;
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-org-detail">
      <Link to="/owner/organizations" className="inline-flex items-center gap-1.5 text-xs text-[#ebeef5]/70 hover:text-[#f1fcff]">
        <ArrowLeft className="h-3 w-3" />Back
      </Link>

      {/* Page header + page-level save bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">{org.name}</h1>
          <p className="text-xs text-[#ebeef5]/70 mt-1 font-mono">slug: {org.slug} · id: {org.id}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border ${
          org.status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
          org.status === "suspended" ? "bg-[#10b7c0]/15 text-[#10b7c0] border-[#10b7c0]/30" :
          "bg-[#ebeef5]/10 text-[#ebeef5]/70 border-white/10"
        }`}>{org.status}</span>
      </div>

      {/* Page-level unsaved-changes bar (above all sections) */}
      <div
        className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition-all ${
          dirty
            ? "bg-[#10b7c0]/10 border-[#10b7c0]/40"
            : "bg-[#0a1530] border-white/10"
        }`}
        data-testid="org-detail-save-bar"
      >
        <div className="flex-1 text-xs">
          {dirty ? (
            <span className="text-[#10b7c0] font-semibold" data-testid="org-detail-dirty-label">Unsaved changes</span>
          ) : (
            <span className="text-[#ebeef5]/70">No pending changes</span>
          )}
          <span className="text-[#ebeef5]/50 ml-2">
            Review before committing. Saves are audit-logged.
          </span>
        </div>
        <button
          onClick={resetPending}
          disabled={!dirty || saving}
          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] disabled:opacity-30"
          data-testid="org-detail-reset"
        >
          Discard
        </button>
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!dirty || saving}
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#10b7c0] text-black hover:bg-[#0ea2aa] disabled:opacity-40 flex items-center gap-1.5"
          data-testid="org-detail-save"
        >
          <Save className="h-3 w-3" /> Save changes
        </button>
      </div>

      {/* Configuration */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-bold text-sm">Configuration</h2>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">Display name</label>
          <input
            value={pending.name}
            onChange={(e) => setPending((p) => ({ ...p, name: e.target.value }))}
            data-testid="org-detail-name"
            className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] focus:outline-none focus:border-[#10b7c0]/50"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">Feature tier</label>
          <select
            value={pending.tier}
            onChange={(e) => onTierChange(e.target.value)}
            data-testid="org-detail-tier"
            className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] focus:outline-none focus:border-[#10b7c0]/50"
          >
            {Object.entries(TIER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="text-[11px] text-[#ebeef5]/50 mt-1">Changing the tier resets feature flags to that tier's preset. You can override individual flags below.</p>
        </div>
      </div>

      {/* Feature flags — interactive checkboxes, pending until Save */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5">
        <h2 className="font-heading font-bold text-sm mb-3">Feature flags</h2>
        <p className="text-xs text-[#ebeef5]/70 mb-4">Per-org overrides on top of the tier preset.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {FEATURE_KEYS.map(([key, label]) => {
            const on = !!pending.flags[key];
            const baseline = !!(org.feature_flags || {})[key];
            const changed = on !== baseline;
            return (
              <label
                key={key}
                className={`flex items-center justify-between gap-3 rounded px-3 py-2 text-xs cursor-pointer border transition-colors ${
                  changed
                    ? "bg-[#10b7c0]/10 border-[#10b7c0]/40"
                    : "bg-[#040b1d] border-white/5 hover:border-white/10"
                }`}
                data-testid={`org-flag-row-${key}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="text-[#ebeef5] block truncate">{label}</span>
                  {changed && (
                    <span className="text-[9px] uppercase tracking-wider text-[#10b7c0] font-semibold">
                      will {on ? "turn ON" : "turn OFF"}
                    </span>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleFlag(key)}
                  className="accent-[#10b7c0] h-4 w-4 cursor-pointer shrink-0"
                  data-testid={`org-flag-toggle-${key}`}
                />
              </label>
            );
          })}
        </div>
      </div>

      {/* Lifecycle */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5">
        <h2 className="font-heading font-bold text-sm mb-3">Lifecycle</h2>
        <div className="flex flex-wrap gap-2">
          {org.status !== "suspended" && org.status !== "archived" && (
            <button
              onClick={() => transition("suspend", "Suspend this organization? All org users will be locked out until you restore it.")}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-[#10b7c0]/30 text-[#10b7c0] hover:bg-[#10b7c0]/10 flex items-center gap-1.5"
              data-testid="org-lifecycle-suspend"
            >
              <PauseCircle className="h-3 w-3" /> Suspend
            </button>
          )}
          {org.status !== "archived" && (
            <button
              onClick={() => transition("archive", "Archive this organization?")}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] hover:border-white/20 flex items-center gap-1.5"
              data-testid="org-lifecycle-archive"
            >
              <Archive className="h-3 w-3" /> Archive
            </button>
          )}
          {org.status !== "active" && (
            <button
              onClick={() => transition("restore")}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1.5"
              data-testid="org-lifecycle-restore"
            >
              <RotateCcw className="h-3 w-3" /> Restore to active
            </button>
          )}
          <button
            onClick={() => setDeleteOpen(true)}
            className="text-xs font-semibold px-3 py-2 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5 ml-auto"
            data-testid="org-lifecycle-delete"
          >
            <Trash2 className="h-3 w-3" /> Delete (irreversible)
          </button>
        </div>
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tier={3}
        realm="owner"
        entityName={org?.name}
        entityType="organization"
        entityId={id}
        onConfirm={performDelete}
      />

      <SystemAdminsPanel orgId={id} />

      {confirmOpen && (
        <ConfirmSaveModal
          org={org}
          pending={pending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={commit}
          saving={saving}
        />
      )}
    </div>
  );
}

function ConfirmSaveModal({ org, pending, onCancel, onConfirm, saving }) {
  const nameDiff = pending.name !== org.name ? { from: org.name, to: pending.name } : null;
  const tierDiff = pending.tier !== org.preset_tier ? { from: org.preset_tier, to: pending.tier } : null;
  const flagDiffs = [];
  for (const [k, label] of FEATURE_KEYS) {
    const now = !!pending.flags[k];
    const before = !!(org.feature_flags || {})[k];
    if (now !== before) flagDiffs.push({ key: k, label, now, before });
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" onClick={onCancel}>
      <div
        className="bg-[#0a1530] border border-white/10 rounded-xl p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="org-save-confirm"
      >
        <h2 className="font-heading font-bold text-lg text-[#f1fcff] mb-1">Review changes</h2>
        <p className="text-xs text-[#ebeef5]/70 mb-5">
          The following will be applied to <span className="text-[#10b7c0]">{org.name}</span> and written to the audit log.
        </p>

        <div className="space-y-4 text-sm">
          {nameDiff && (
            <div data-testid="confirm-name">
              <p className="text-[10px] uppercase tracking-wider text-[#ebeef5]/70 font-semibold mb-1">Name</p>
              <p className="text-[#ebeef5]">
                <span className="line-through text-[#ebeef5]/50">{nameDiff.from}</span>
                <span className="mx-2 text-[#10b7c0]">→</span>
                <span className="font-semibold">{nameDiff.to}</span>
              </p>
            </div>
          )}
          {tierDiff && (
            <div data-testid="confirm-tier">
              <p className="text-[10px] uppercase tracking-wider text-[#ebeef5]/70 font-semibold mb-1">Feature tier</p>
              <p className="text-[#ebeef5]">
                <span className="line-through text-[#ebeef5]/50">{TIER_LABEL[tierDiff.from]}</span>
                <span className="mx-2 text-[#10b7c0]">→</span>
                <span className="font-semibold">{TIER_LABEL[tierDiff.to]}</span>
              </p>
            </div>
          )}
          {flagDiffs.length > 0 && (
            <div data-testid="confirm-flags">
              <p className="text-[10px] uppercase tracking-wider text-[#ebeef5]/70 font-semibold mb-2">Feature flags</p>
              <ul className="space-y-1">
                {flagDiffs.map((d) => (
                  <li key={d.key} className="flex items-center gap-2 text-xs" data-testid={`confirm-flag-${d.key}`}>
                    {d.now ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                    )}
                    <span className="text-[#ebeef5]">{d.label}</span>
                    <span className={`ml-auto text-[10px] uppercase font-semibold ${d.now ? "text-emerald-400" : "text-red-400"}`}>
                      {d.now ? "ON" : "OFF"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!nameDiff && !tierDiff && flagDiffs.length === 0 && (
            <p className="text-xs text-[#ebeef5]/50">No changes to apply.</p>
          )}
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]"
            data-testid="confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving || (!nameDiff && !tierDiff && flagDiffs.length === 0)}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-[#10b7c0] text-black hover:bg-[#0ea2aa] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="confirm-save"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            Confirm and save
          </button>
        </div>
      </div>
    </div>
  );
}


function SystemAdminsPanel({ orgId }) {
  const [admins, setAdmins] = useState(null);
  const [resetting, setResetting] = useState(null);      // admin id in flight
  const [results, setResults] = useState({});            // { [adminId]: {email, temporary_password, copied} }

  const load = async () => {
    try {
      const { data } = await ownerApi.get(`/organizations/${orgId}/admins`);
      setAdmins(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load admins");
      setAdmins([]);
    }
  };

  useEffect(() => { load(); }, [orgId]); // eslint-disable-line

  const resetOne = async (admin) => {
    if (resetting) return;
    setResetting(admin.id);
    try {
      const { data } = await ownerApi.post(`/organizations/${orgId}/admins/${admin.id}/reset-password`);
      setResults((prev) => ({
        ...prev,
        [admin.id]: { email: data.email, temporary_password: data.temporary_password, copied: false },
      }));
      toast.success(`Temporary password generated for ${data.email}`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(null);
    }
  };

  const copy = async (adminId, pw) => {
    try {
      await navigator.clipboard.writeText(pw);
      setResults((prev) => ({ ...prev, [adminId]: { ...prev[adminId], copied: true } }));
      setTimeout(() => {
        setResults((prev) => {
          const cur = prev[adminId];
          if (!cur) return prev;
          return { ...prev, [adminId]: { ...cur, copied: false } };
        });
      }, 1800);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const dismiss = (adminId) => {
    setResults((prev) => {
      const next = { ...prev };
      delete next[adminId];
      return next;
    });
  };

  return (
    <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5" data-testid="org-system-admins">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-[#f1fcff]">System Admins</h2>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/50">
          {admins?.length ?? "—"} account{(admins?.length ?? 0) === 1 ? "" : "s"}
        </p>
      </div>

      {admins === null ? (
        <div className="flex items-center gap-2 py-3 text-xs text-[#ebeef5]/70">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[#10b7c0]" />
          Loading…
        </div>
      ) : admins.length === 0 ? (
        <p className="text-xs text-[#ebeef5]/50 py-2">No System Admins on this organization yet.</p>
      ) : (
        <ul className="divide-y divide-white/5">
          {admins.map((admin) => {
            const result = results[admin.id];
            const isResetting = resetting === admin.id;
            return (
              <li
                key={admin.id}
                className="py-3 first:pt-0 last:pb-0"
                data-testid={`org-admin-row-${admin.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#f1fcff] font-medium truncate">
                      {admin.full_name || admin.email}
                    </p>
                    <p className="text-[11px] text-[#ebeef5]/50 font-mono truncate">{admin.email}</p>
                  </div>
                  <button
                    onClick={() => resetOne(admin)}
                    disabled={isResetting}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md border border-[#10b7c0]/40 text-[#10b7c0] hover:bg-[#10b7c0]/10 disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                    data-testid={`org-admin-reset-${admin.id}`}
                  >
                    {isResetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                    Reset password
                  </button>
                </div>
                {result && (
                  <div
                    className="mt-3 rounded border border-[#10b7c0]/30 bg-[#10b7c0]/5 px-3 py-2.5 text-xs"
                    data-testid={`org-admin-temp-pw-${admin.id}`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#10b7c0]">
                        Temporary password
                      </p>
                      <button
                        onClick={() => dismiss(admin.id)}
                        className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/50 hover:text-[#ebeef5]/80"
                        data-testid={`org-admin-temp-pw-dismiss-${admin.id}`}
                      >
                        Dismiss
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 min-w-0 font-mono text-sm text-[#f1fcff] bg-[#040b1d]/60 px-2 py-1.5 rounded break-all select-all">
                        {result.temporary_password}
                      </code>
                      <button
                        onClick={() => copy(admin.id, result.temporary_password)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] flex items-center gap-1.5 shrink-0"
                        data-testid={`org-admin-temp-pw-copy-${admin.id}`}
                      >
                        {result.copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        {result.copied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p className="mt-2 text-[10px] text-[#ebeef5]/50 leading-relaxed">
                      Shown once. Hand off securely and instruct the admin to rotate it on first login.
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

